import { Stack } from "@carbon/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string } from "zod";

import { FilterableSinglePickerFormField } from "src/components/Form/FilterableSinglePickerFormField";
import { TextAreaFormField } from "src/components/Form/TextAreaFormField.tsx";
import { TextInputFormField } from "src/components/Form/TextInputFormField.tsx";
import { ConfirmActionModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { CODE_REGEX } from "src/constants/common";
import { mapToModalError } from "src/errors";
import { createAdvancedRuleMutation } from "src/graphql/anti-fraud/queries/advancedRules/mutations/CreateAdvancedRuleMutation.ts";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useGetRuleBusinessTypeLabel } from "src/hooks/rules/useGetRuleBusinessTypeLabel";
import { useT } from "src/internationalization";
import { mapToApiRuleBusinessType } from "src/mappers/rule.mappers";
import { Rule, RuleBusinessType, RuleBusinessTypeValues } from "src/types/rule";

export type CreateRuleFormPayload = {
  code: string;
  name: string;
  description: string;
  query: string;
  businessType: RuleBusinessType | null;
};

type CreateRuleAdvancedModalProps = {
  onClose: () => void;
  caseTypeCode: string;
  rules: Rule[];
};

export const CreateRuleAdvancedModal: FC<CreateRuleAdvancedModalProps> = ({
  onClose,
  caseTypeCode,
  rules,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();
  const getRuleBusinessTypeLabel = useGetRuleBusinessTypeLabel();

  const validationSchema = useSchema(rules);

  const methods = useForm<CreateRuleFormPayload>({
    defaultValues: {
      code: "",
      name: "",
      description: "",
      query: "",
      businessType: null,
    },
    resolver: zodResolver(validationSchema),
  });

  const { handleSubmit } = methods;

  const {
    performMutation: createAdvancedRule,
    loading,
    error,
  } = usePerformMutation(createAdvancedRuleMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.create.toast.success.message"));
      onClose();
    },
    mutationOption: {
      refetchQueries: [getRulesQuery],
      awaitRefetchQueries: true,
    },
  });

  const onSubmit: SubmitHandler<CreateRuleFormPayload> = async (data) => {
    await createAdvancedRule({
      code: data.code,
      name: data.name,
      description: data.description,
      query: data.query!,
      businessType: mapToApiRuleBusinessType(data.businessType!),
      caseTypeCode: caseTypeCode,
    });
  };

  return (
    <ConfirmActionModal
      error={mapToModalError(error)}
      onClose={onClose}
      size="md"
      loading={loading}
      title={t("page.rules.advanced.modal.create.title")}
      confirmButtonText={t("general.actions.confirm")}
      onConfirm={handleSubmit(onSubmit)}
      initialFocus="#name"
    >
      <FormProvider {...methods}>
        <Stack gap={5}>
          <TextInputFormField
            id="name"
            name="name"
            control={methods.control}
            label={t("general.form.name.label")}
            helperText={t("page.rules.modal.create.form.name.helper")}
          />
          <TextInputFormField
            id="code"
            name="code"
            control={methods.control}
            label={t("general.form.code.label")}
            helperText={t("page.rules.modal.create.form.code.helper")}
          />

          <TextAreaFormField
            id="description"
            label={t("general.form.description.label")}
            helperText={t("page.rules.modal.create.form.description.helper")}
            name="description"
            control={methods.control}
          />
          <FilterableSinglePickerFormField
            id="businessType"
            name="businessType"
            control={methods.control}
            options={[...RuleBusinessTypeValues]}
            getOptionLabel={(item) => getRuleBusinessTypeLabel(item)}
            getOptionValue={(item) => item}
            label={t("page.rules.advanced.modal.create.form.businessType")}
            rules={{ required: t("general.form.field.required") }}
          />

          <TextAreaFormField
            id="query"
            name="query"
            control={methods.control}
            label={t("page.rules.advanced.modal.create.form.query")}
            rules={{ required: t("general.form.field.required") }}
          />
        </Stack>
      </FormProvider>
    </ConfirmActionModal>
  );
};

const useSchema = (rules: Rule[]) => {
  const t = useT();

  return useMemo(() => {
    return object({
      name: string()
        .min(1, t("general.form.name.error.required"))
        .refine((value) => !rules.some((rule) => rule.name === value), {
          message: t("general.form.name.error.unique"),
        }),
      code: string()
        .min(1, t("general.form.code.error.required"))
        .regex(CODE_REGEX, t("general.form.code.error.invalid"))
        .refine((value) => !rules.some((rule) => rule.code === value), {
          message: t("general.form.code.error.unique"),
        }),
      description: string().min(1, t("general.form.description.error.required")),
      businessType: string({ message: t("general.form.field.required") }),
      query: string().min(1, t("general.form.field.required")),
    });
  }, [rules, t]);
};
